import { compareFichas, createEmptyFicha, type FichaCata } from '@escuela/ficha-domain'
import type { FichaContentBody, ReferenceFichaDto } from './api'

/**
 * Predicado minimo para PUBLICAR una ficha de referencia.
 *
 * El `fichaContentSchema` del dominio es estructural (todos los campos son
 * strings/numbers/arrays sin `min`/`required`), asi que una ficha vacia lo
 * pasa. Publicar una ficha en blanco la ofreceria a los alumnos como referencia
 * inutil para comparar. Exigimos, como minimo:
 *   1) nombre del vino (`vino`) no vacio, y
 *   2) al menos un campo sensorial comparable respondido.
 *
 * Reutilizamos el motor de comparacion del dominio: comparando la ficha contra
 * si misma, `comparable` es la cantidad de campos que un alumno podria comparar
 * contra esta referencia (0 => no sirve como referencia). Un solo lugar, sin
 * duplicar la logica entre new.tsx y [id].tsx.
 */
export function isPublishableFicha(ficha: FichaCata): boolean {
  if (ficha.vino.trim() === '') return false
  return compareFichas(ficha, ficha).comparable > 0
}

/** Extrae el contenido evaluable (sin metadatos de sync) de una FichaCata. */
export function toContentBody(ficha: FichaCata): FichaContentBody {
  return {
    vino: ficha.vino,
    cosecha: ficha.cosecha,
    origen: ficha.origen,
    alcPorcentaje: ficha.alcPorcentaje,
    apariencia: ficha.apariencia,
    nariz: ficha.nariz,
    gusto: ficha.gusto,
    conclusiones: ficha.conclusiones,
  }
}

/**
 * Reconstruye una FichaCata editable a partir del DTO del server, arrancando de
 * una ficha vacia para garantizar que todos los sub-campos existen (lectura
 * tolerante de fichas viejas).
 */
export function fromDto(dto: ReferenceFichaDto): FichaCata {
  const empty = createEmptyFicha()
  return {
    ...empty,
    id: dto.id,
    userId: dto.userId,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    vino: dto.vino,
    cosecha: dto.cosecha,
    origen: dto.origen,
    alcPorcentaje: dto.alcPorcentaje,
    apariencia: { ...empty.apariencia, ...dto.apariencia },
    nariz: { ...empty.nariz, ...dto.nariz },
    gusto: { ...empty.gusto, ...dto.gusto },
    conclusiones: { ...empty.conclusiones, ...dto.conclusiones },
    authorRole: dto.authorRole,
    isReference: dto.isReference,
    status: dto.status,
  }
}
